import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Skeleton } from '@mui/material';
import { buildCategoryCards, buildCategoryChips, buildPersonalizedCards } from '../aiPromptSuggestions';
import { FOR_YOU_CATEGORY_ID, PROMPT_CARDS_PER_TAB, PROMPT_CATEGORIES } from '../promptLibrary';
import PromptSuggestionCard from './PromptSuggestionCard';
import css from './PromptSuggestions.css';

const FALLBACK_CATEGORY_ID = PROMPT_CATEGORIES[0].id;

const SKELETON_CARD_COUNT = PROMPT_CARDS_PER_TAB;

const renderSkeletonCards = () =>
    Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => (
        <div key={index} className={css.skeletonCard}>
            <Skeleton variant="rectangular" className={css.skeletonLabel} />
            <Skeleton variant="rectangular" className={css.skeletonDescription} />
        </div>
    ));

const PromptSuggestions = ({ personalized = null, isLoading, onPick, translate }) => {
    const [pickedCategoryId, setPickedCategoryId] = useState(FOR_YOU_CATEGORY_ID);

    const hasForYou = isLoading || Boolean(personalized?.length);
    const chips = buildCategoryChips({ hasPersonalized: hasForYou, translate });
    const selectedCategoryId = chips.some(({ id }) => id === pickedCategoryId)
        ? pickedCategoryId
        : FALLBACK_CATEGORY_ID;

    const isForYou = selectedCategoryId === FOR_YOU_CATEGORY_ID;
    const cards = isForYou ? buildPersonalizedCards(personalized) : buildCategoryCards(selectedCategoryId, translate);

    return (
        <div className={css.promptSuggestions}>
            <div className={css.categoryChips}>
                {/* Plain buttons on purpose: the Button atom's flat :hover rule strips the 1px border,
                    which resizes the chip and makes the whole centered layout jump on every hover. */}
                {chips.map(({ id, label }) => (
                    <button
                        type="button"
                        key={id}
                        aria-pressed={id === selectedCategoryId}
                        onClick={() => setPickedCategoryId(id)}
                        className={classNames(css.categoryChip, {
                            [css.categoryChipSelected]: id === selectedCategoryId,
                        })}
                    >
                        {label}
                    </button>
                ))}
            </div>

            <div className={css.cardGrid} key={selectedCategoryId}>
                {isForYou && isLoading
                    ? renderSkeletonCards()
                    : cards.map(card => <PromptSuggestionCard key={card.id} card={card} onPick={onPick} />)}
            </div>
        </div>
    );
};

PromptSuggestions.propTypes = {
    personalized: PropTypes.arrayOf(
        PropTypes.shape({
            text: PropTypes.string.isRequired,
            label: PropTypes.string,
            tag: PropTypes.string,
            icon: PropTypes.string,
        })
    ),
    isLoading: PropTypes.bool.isRequired,
    onPick: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default PromptSuggestions;
